import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import { type SafeFetchResponse } from '@/api'
import { getEdibleRecommendations, getInhalableRecommendations, getScentRecommendations } from '@/api/widget'
import type { Setter } from '@/types'
import { WidgetStep } from '@/types'
import {
  CombinedRecommendationFilters,
  EdibleFilters,
  EdibleRecommendationResponse,
  InhalableRecommendationResponse,
  MenuType,
  RecommendationFilters,
  RecommendationRequest,
} from '@/types/widget'
import { isLocal, showErrorToast } from '@/utils'
import { getDeviceId, isMobile } from '@/utils/device'
import { logger } from '@/utils/logger'
import { createPersistOptions } from '@/utils/zustand-persist'
import { track } from '@/utils/analytics'
import { getContactID, getWidgetSettings, useSessionStore } from './useSessionStore'
import { useFlowState } from './useFlowState'

type RecommendationStoreState = {
  loading: boolean
  loaded: boolean
  data: InhalableRecommendationResponse | EdibleRecommendationResponse | undefined
  error: string | undefined
  filters: CombinedRecommendationFilters
}

type RecommendationStoreActions = {
  setFilters: (filters: Setter<CombinedRecommendationFilters>) => void
  submitRequest: () => Promise<InhalableRecommendationResponse | EdibleRecommendationResponse | undefined>
  resetRequest: () => void // Clears the request, not the history
}

type RecommendationStore = RecommendationStoreState & RecommendationStoreActions

const initialState: RecommendationStoreState = {
  loading: false,
  loaded: false,
  data: undefined,
  error: undefined,
  filters: {},
}

const MIN_LOADING_TIME = 1000
const PERSIST_RECOMMENDATION_DATA = true

export const useRecommendationStore = create<RecommendationStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setFilters: (filters: Setter<CombinedRecommendationFilters>) => {
          const currentFilters = get().filters
          const newFilters = typeof filters === 'function' ? filters(currentFilters ?? {}) : filters
          logger.log('setFilters', newFilters)
          set({ filters: newFilters, data: undefined, loaded: false }, false, 'setFilters')
        },
        submitRequest: async (): Promise<
          InhalableRecommendationResponse | EdibleRecommendationResponse | undefined
        > => {
          const filters = get().filters
          if (!filters) {
            return undefined
          }

          set({ loading: true }, false, 'submitRequest/start')

          const baseFlowType = getWidgetSettings()?.recommendationSettings?.flowType ?? 'default'
          const isEdiblesFlow = isEdiblesFlowSelected()
          const flowType = isEdiblesFlow ? 'edible' : baseFlowType === 'default' ? 'inhalable' : 'scents'

          // The rec engine rejects edible requests without a dosage. Guard here so a
          // stale persisted filter or skipped step routes the user back to Potency
          // instead of producing a confusing 422.
          if (flowType === 'edible' && !(filters as EdibleFilters)?.dosage) {
            set(
              { loading: false, loaded: false, error: undefined },
              false,
              'submitRequest/missing-dosage'
            )
            useFlowState.getState().setCurrentStep(WidgetStep.Potency)
            showErrorToast('Please choose a dosage to continue')
            return undefined
          }

          const payload = {
            deviceId: getDeviceId(),
            contactID: getContactID(),
            filters,
          } as RecommendationRequest

          const callback: (
            uid: string,
            globalId: string,
            menuType: MenuType,
            payload: RecommendationRequest
          ) => Promise<SafeFetchResponse<InhalableRecommendationResponse | EdibleRecommendationResponse>> =
            flowType === 'edible'
              ? getEdibleRecommendations
              : flowType === 'inhalable'
                ? getInhalableRecommendations
                : getScentRecommendations

          const { currentUID, currentGlobalID, currentMenuType } = useSessionStore.getState()
          if (!currentUID || !currentGlobalID) {
            showErrorToast('No current UID or globalID set')
            return undefined
          }

          const startTime = performance.now()

          const [data, error] = await callback(
            currentUID ?? '',
            currentGlobalID ?? '',
            currentMenuType ?? 'both',
            payload
          )

          const endTime = performance.now()
          const duration = endTime - startTime
          logger.log('submitRequest', 'duration', duration)
          // We want at least MIN_LOADING_TIME of loading time, so we make up the difference
          if (duration < MIN_LOADING_TIME) {
            logger.log('submitRequest', 'waiting for loading time', MIN_LOADING_TIME - duration)
            await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_TIME - duration))
          }

          logger.log('submitRequest', 'loading time complete')

          set({ data, error, loaded: !error, loading: false }, false, 'submitRequest/complete')

          if (data && !error) {
            // const products = 'products' in data ? data.products : undefined
          }

          if (error && (!isMobile() || isLocal())) {
            showErrorToast(error || 'Error submitting recommendation request')
          }

          return data
        },
        resetRequest: () =>
          set(
            {
              filters: {},
              loading: false,
              loaded: false,
              data: undefined,
              error: undefined,
            },
            false,
            'resetRequest'
          ),
      }),
      createPersistOptions<RecommendationStore, 'filters' | 'data' | 'loaded'>({
        name: 'recommendation',
        pick: PERSIST_RECOMMENDATION_DATA ? ['filters', 'data', 'loaded'] : ['filters'],
        storageType: 'session',
      })
    ),
    { name: 'RecommendationStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

export function getFilters<T extends RecommendationFilters>(): T {
  return useRecommendationStore.getState().filters as T
}

export function isEdiblesFlowSelected({ category = [] }: CombinedRecommendationFilters = getFilters()) {
  return category?.some(
    (category) => category.toLowerCase().trim() === 'edibles' || category.toLowerCase().trim() === 'edible'
  )
}
